// 🔧 entries.js
// ✅ Purpose: Public-facing catalog page showing all event entries that may be voted on
// ✅ Features:
// - Uses event slug context to load the active event
// - Pulls entries from /events/{eventId}/cars
// - Pulls categories from /events/{eventId}/categories
// - Groups entries by category
// - Sorts categories alphabetically
// - Sorts entries by entry number when available
// - Shows entry image, number, description, owner name, and category
// - Links each entry to a public detail page
// - Uses a clean two-tile layout per category
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { useVotingSettings } from "../../hooks/use_voting_settings";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
import { getEntryRegistrationLabel } from "../../utils/entry_registration_label";
import { getEntryImageUrl } from "../../utils/demo_entry_assets";
import { getEntryPath } from "../../utils/entry_urls";

const Entries = () => {

  // ✅ Event context gives us the resolved eventId from the URL slug
  const { eventslug, eventId, eventDoc, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  // ✅ Page data state
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const { settings, loading: settingsLoading } = useVotingSettings(eventId);
  // ✅ Loading / notification state
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  // ✅ Load categories and entries for this event
  useEffect(() => {
    const loadEntriesPage = async () => {
      if (!eventId) return;

      try {
        console.log("📋 Loading public entries page for event:", eventId);

        setLoading(true);
        setStatus(null);

        // ✅ Load categories
        const categoriesRef = collection(db, "events", eventId, "categories");
        const categoriesSnap = await getDocs(categoriesRef);

        const loadedCategories = categoriesSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // ✅ Load entries
        // Note: orderBy regcnt is safe if most/all entries have it.
        // If Firestore complains about missing fields later, we can remove orderBy and sort client-side only.
        const entriesRef = collection(db, "events", eventId, "cars");
        const entriesQuery = query(entriesRef, orderBy("regcnt", "asc"));
        const entriesSnap = await getDocs(entriesQuery);

        const loadedEntries = entriesSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log("✅ Loaded public entries:", loadedEntries.length);
        console.log("✅ Loaded public entry categories:", loadedCategories.length);

        setCategories(loadedCategories);
        setEntries(loadedEntries);
      } catch (error) {
        console.error("❌ Failed to load public entries page:", error);

        setStatus({
          type: "danger",
          message: "Unable to load entries right now. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadEntriesPage();
  }, [eventId]);

  // ✅ Auto-clear status after 20 seconds
  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

    useEffect(() => {
    if (!ready || !eventId) return;

    trackEvent({
      action: GA_EVENTS.ENTRY_LIST_VIEWED,
      parameters: {
        event_slug: eventslug,
        event_id: eventId,
        entry_count: entries.length,
        category_count: categories.length,
        source_page: window.location.pathname,
      },
    });
  }, [ready, eventId, eventslug, entries.length, categories.length]);
  
  // =====================================================
  // 🔎 Dynamic SEO
  // =====================================================

  const breadcrumbs = [
    { name: "Home", path: "/" },
    {
      name: eventDoc?.name || "Event",
      path: `/${eventslug}/event`,
    },
    {
      name: "Entries",
      path: `/${eventslug}/entries`,
    },
  ];

  const approvedEntriesSeo = buildEventPageSeo({
    eventDoc,
    pageKey: "entries",

    fallbackTitle: `${eventDoc?.name || "Event"} Entries | Let's Go Vote`,

    fallbackDescription:
      `Browse event entries for ${
        eventDoc?.name || "this event"
      }.`,
  });

  const eventSeo = {
    ...approvedEntriesSeo,

    path: `/${eventslug}/entries`,

    breadcrumbs,
  };

  // ✅ Map category id/name for display
  const categoryMap = useMemo(() => {
    const map = {};

    categories.forEach((category) => {
      map[String(category.id)] =
        category.name ||
        category.category ||
        category.title ||
        "Uncategorized";
    });

    return map;
  }, [categories]);

  // ✅ Group entries by category
  const groupedEntries = useMemo(() => {
    const grouped = {};

    entries.forEach((entry) => {
      const categoryId = String(entry.catid || "uncategorized");
      const categoryName = categoryMap[categoryId] || "Uncategorized";

      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }

      grouped[categoryName].push(entry);
    });

    // ✅ Sort entries within each category by entry number, then description
    Object.keys(grouped).forEach((categoryName) => {
      grouped[categoryName].sort((a, b) => {
        const aNum = Number(a.regcnt || 0);
        const bNum = Number(b.regcnt || 0);

        if (aNum !== bNum) return aNum - bNum;

        return String(a.description || "").localeCompare(
          String(b.description || "")
        );
      });
    });

    return grouped;
  }, [entries, categoryMap]);

  // ✅ Sorted category names
  const sortedCategoryNames = useMemo(() => {
    return Object.keys(groupedEntries).sort((a, b) => a.localeCompare(b));
  }, [groupedEntries]);

  const getSettingsDate = (value) => {
    if (!value) return null;

    if (value?.toDate) {
      return value.toDate();
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const publicEntryStartDate = getSettingsDate(settings?.publicEntryStart);
  const publicEntryEndDate = getSettingsDate(settings?.publicEntryEnd);
  const now = new Date();

  const publicEntryWindowIsConfigured =
    Boolean(publicEntryStartDate) && Boolean(publicEntryEndDate);

  const publicEntrySubmissionIsOpen =
    eventDoc?.allowOnlineEntries === true &&
    (!publicEntryWindowIsConfigured ||
      (now >= publicEntryStartDate && now <= publicEntryEndDate));

  const publicEntryEndLabel = publicEntryEndDate
    ? publicEntryEndDate.toLocaleString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  // ✅ Page loading gate
  if (eventLoading || !ready || loading || settingsLoading) {
    return <FullPageSpinner message="Loading entries..." />;
  }

  return (
    <>
      <SeoHead {...eventSeo} />
      <section id="top-content" className="container-fluid py-4">
      <div className="container" style={{ maxWidth: "1100px" }}>
        {/* ✅ Page Header */}
        <div className="text-center mb-4">
          <h1 className="fw-bold mb-2">Entries</h1>

          <p className="text-muted mb-0">
            Browse the entries for {eventDoc?.name || "this event"}.
          </p>
        </div>

        {/* ✅ Status Message */}
        {status && (
          <div className={`alert alert-${status.type} text-center`}>
            {status.message}
          </div>
        )}

        {/* ✅ Empty State */}
        {entries.length === 0 && (
          <div className="alert alert-info text-center">
            No entries have been added yet.
          </div>
        )}

        {publicEntrySubmissionIsOpen && (
          <div className="alert alert-success text-center">
            <div className="fw-bold mb-2">
              📝 Entry submissions are open.
            </div>

            {publicEntryEndLabel && <div className="mb-3">
              Last day to submit entries: <strong>{publicEntryEndLabel}</strong>
            </div>}

            <Link
              to={`/${eventslug}/public_entry`}
              className="btn btn-primary"
            >
              Submit an Entry
            </Link>
          </div>
        )}

        {/* ✅ Category Sections */}
        {sortedCategoryNames.map((categoryName) => (
          <div key={categoryName} className="card shadow-sm mb-4">
            {/* ✅ Category Header */}
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h2 className="h5 fw-bold mb-0">{categoryName}</h2>

              <span className="badge bg-secondary">
                {groupedEntries[categoryName].length} entries
              </span>
            </div>

            {/* ✅ Category Body */}
            <div className="card-body">
              <div className="row g-4">
                {groupedEntries[categoryName].map((entry) => (
                  <div key={entry.id} className="col-12 col-md-6">
                    <div className="card h-100 shadow-sm">
                      {/* ✅ Entry Image */}
                      {getEntryImageUrl(entry, eventDoc) ? (
                        <img
                          src={getEntryImageUrl(entry, eventDoc)}
                          alt={entry.description || "Entry"}
                          className="card-img-top"
                          style={{
                            height: "220px",
                            objectFit: "cover",
                            width: "100%",
                          }}
                        />
                      ) : (
                        <div
                          className="bg-light d-flex align-items-center justify-content-center text-muted border-bottom"
                          style={{ height: "220px" }}
                        >
                          No Image
                        </div>
                      )}

                      {/* ✅ Entry Content */}
                      <div className="card-body d-flex flex-column">
                        <div className="mb-2">
                          <span className="badge bg-dark">
                            {getEntryRegistrationLabel(entry)}
                          </span>
                        </div>

                        <h3 className="h5 fw-bold mb-2">
                          {entry.description ||
                            [entry.year, entry.model].filter(Boolean).join(" ") ||
                            "Untitled Entry"}
                        </h3>

                        {(entry.name || entry.ownerFirst || entry.ownerLast) && (
                          <p className="text-muted mb-3">
                            {entry.name ||
                              [entry.ownerFirst, entry.ownerLast]
                                .filter(Boolean)
                                .join(" ")}
                          </p>
                        )}

                        <div className="mt-auto">
                          <Link
                            to={getEntryPath(eventslug, entry)}
                            className="btn btn-primary w-100"
                          >
                            View Entry
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
    </>
  );
};

export default Entries;
